import { ReactElement, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useUrlParams from '../hooks/utility/useUrlParams';
import { UTMInterface } from '../services/exports/Interfaces';
import { ENVIRONMENTS } from '../services/exports/Constants';
import ReactPixel from 'react-facebook-pixel';
import useInitialData from '../hooks/global/useInitialData';
import { setUtm } from '../services/exports/Cookies';
import collect from 'collect.js';
import { getStorefrontOrigin } from '../modules/storefront/exports/Cookies';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import { StoreInterface } from '../store/types';
import { hasTrackingConsent } from '../utils/cookieConsent';

interface Props {
  children: ReactElement;
}

export default function TrackingProvider({ children }: Props) {
  const params = useUrlParams();
  const { isMobileApp } = useMobileApp();
  const { data } = useInitialData();
  const { franchise } = data;
  const { cookiesConsent } = useSelector((state: StoreInterface) => state.appStatus);
  const hasConsent = hasTrackingConsent(cookiesConsent, isMobileApp);

  // The landing page's attribution, captured once on mount: the visitor may accept the banner after
  // navigating away from the URL that carried it.
  const landingRef = useRef<{ referrer: string | null; utmQueryParams: UTMInterface }>(null);

  if (landingRef.current === null) {
    landingRef.current = {
      referrer: document.referrer ? new URL(document.referrer).origin : null,
      utmQueryParams: {
        utm_source: isMobileApp ? 'mobile_app' : params?.utm_source,
        utm_medium: params?.utm_medium,
        utm_campaign: params?.utm_campaign,
        utm_tracking_link_id: params?.utm_tracking_link_id,
        gclid: params?.gclid,
        fbclid: params?.fbclid,
      },
    };
  }

  const blacklist = ['paypal'];

  const saveUtm = (): void => {
    const { referrer, utmQueryParams } = landingRef.current;

    if (referrer && blacklist.some((item) => referrer.includes(item))) return;

    if (
      collect(utmQueryParams)
        .values()
        .filter((item) => !!item)
        .isNotEmpty()
    ) {
      return setUtm(utmQueryParams);
    }

    if (getStorefrontOrigin()) return;

    if (referrer) {
      setUtm({
        ...utmQueryParams,
        utm_source: referrer,
      });
    }
  };

  // GDPR (§25 TDDDG): the `utm` cookie stores ad click ids (gclid/fbclid) on the device, so it is only
  // written after an explicit accept, and removed again on anything else.
  useEffect(() => {
    if (!hasConsent) {
      setUtm(null);

      return;
    }

    saveUtm();
  }, [hasConsent]);

  // GDPR: the Pixel loads fbevents.js and pings facebook.com, so it waits for an explicit accept.
  useEffect(() => {
    if (hasConsent && franchise?.facebook_pixel_id) {
      ReactPixel.init(franchise.facebook_pixel_id, null, {
        autoConfig: true,
        debug: import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD,
      });
      ReactPixel.pageView();
    }
  }, [franchise?.facebook_pixel_id, hasConsent]);

  return children;
}
